import { CREW_ORDER, type CrewEntry, type CrewKey, type World, type WorldPage } from "../crewData";

/**
 * The German crew (bidmio.de). Same fourteen figures as everywhere, German
 * trades and German Handwerker talk: the Stuckateur stands where the Slovak
 * façade worker stands, the Bauträger where the developer stands.
 * Every answer repeats only what the German product pages already say
 * (offene Posten und Mahnwesen, GiroCode, Tagesplan, Firmenplan, Lager,
 * Nachkalkulation, Abschlagsrechnung mit Einbehalt, Anwesenheit per QR-Code).
 */
export const DE_CREW: Record<CrewKey, CrewEntry> = {
  mason: {
    label: "Maurer",
    home: "survive",
    survive: { quote: "Die Löhne krieg ich diesen Monat wieder nicht zusammen.", answer: "Offene Posten auf einen Blick: wer Ihnen wie viel schuldet. Die Zahlungserinnerung geht direkt aus Bidmio raus." },
    grow: { quote: "Drei Baustellen gleichzeitig – und keine Ahnung, wo meine Leute sind.", answer: "Der Tagesplan zeigt, wer in dieser Woche auf welchem Auftrag ist." },
  },
  carpenter: {
    label: "Zimmerer",
    home: "grow",
    survive: { quote: "Holz bezahlt. Mein Geld? Noch nicht.", answer: "Die Anzahlungsrechnung geht raus, bevor das Holz bestellt wird." },
    grow: { quote: "Auftragsbuch voll bis Frühjahr. Ich komm nicht hinterher.", answer: "Der Firmenplan zeigt Aufträge und Kapazitäten im ganzen Betrieb." },
  },
  electrician: {
    label: "Elektriker",
    home: "survive",
    survive: { quote: "Der Billigheimer war ein Drittel günstiger.", answer: "Ein sauberes Angebot, das der Kunde digital freigibt. Daraus wird der Auftrag, ohne neu zu tippen." },
    grow: { quote: "Wo sind die 40 Meter NYM hin?", answer: "Material aus dem Lager direkt auf den Auftrag buchen. Es erscheint in der Nachkalkulation." },
  },
  plumber: {
    label: "Installateur",
    home: "grow",
    survive: { quote: "Rohrbruch nachts um zwei. Abgerechnet nie.", answer: "Stunden und Material hängen am Auftrag. Die Rechnung entsteht aus dem, was schon dran hängt." },
    grow: { quote: "Montage, Kundendienst, Leute – alles im Kopf.", answer: "Technikereinsätze und Tagesplan an einem Ort, nicht im Kopf." },
  },
  painter: {
    label: "Maler",
    home: "survive",
    survive: { quote: "Seit zwei Monaten warte ich aufs Geld.", answer: "Rechnung mit GiroCode für die Überweisung. Und Sie sehen, was noch offen ist." },
    grow: { quote: "Die Kolonne streicht – nur wo genau?", answer: "Anwesenheit per QR-Code auf der Baustelle. Sie sehen, wer wo angemeldet ist." },
  },
  roofer: {
    label: "Dachdecker",
    home: "grow",
    survive: { quote: "Es regnet, die Jungs stehen, die Löhne laufen.", answer: "Die mitlaufende Nachkalkulation zeigt, was ein Auftrag wirklich gekostet hat." },
    grow: { quote: "Zwei krank, das Dach ist offen.", answer: "Urlaub und Abwesenheiten im Firmenplan. Sie sehen, wer fehlt." },
  },
  service: {
    label: "Kundendienst",
    home: "survive",
    survive: { quote: "Diese Woche ist der Kalender leer.", answer: "Kunden und frühere Aufträge an einem Ort. Sie wissen, wen Sie anrufen." },
    grow: { quote: "Sechs Monteure, und ich spiel den Disponenten.", answer: "Technikereinsätze, Tagesplan und Unterschrift vor Ort." },
  },
  siteManager: {
    label: "Bauleiter",
    home: "grow",
    survive: { quote: "Der GU behält schon wieder den Sicherheitseinbehalt.", answer: "Abschlagsrechnung mit Fertigstellungsgrad und Einbehalt, ohne Rechnen von Hand. Die Fristen behalten Sie selbst im Blick." },
    grow: { quote: "Umsatz rauf, Konto runter.", answer: "Mitlaufende Nachkalkulation pro Auftrag: Sie sehen den Gewinn, nicht nur den Umsatz." },
  },
  owner: {
    label: "Inhaber",
    home: "grow",
    survive: { quote: "Bauherr zahlt nicht, und ich hab 30 Löhne.", answer: "Offene Posten und Mahnwesen: wer schuldet und welche Erinnerung schon raus ist." },
    grow: { quote: "40 Leute und ich weiß nicht, welche Baustelle Geld bringt.", answer: "Soll-Ist-Vergleich pro Auftrag und der Firmenplan an einem Ort." },
  },
  tiler: {
    label: "Fliesenleger",
    home: "survive",
    survive: { quote: "Die teureren Fliesen hab am Ende ich bezahlt.", answer: "Den Nachtrag halten Sie am Auftrag fest. Er kommt mit auf die Rechnung." },
    grow: { quote: "Drei Wohnungen gleichzeitig, Material überall.", answer: "Material wird auf den Auftrag gebucht. Sie sehen, was wo verbaut wurde." },
  },
  joiner: {
    label: "Tischler",
    home: "grow",
    survive: { quote: "Küche steht. Restzahlung? Fehlanzeige.", answer: "Anzahlung, Abschlagsrechnung und Schlussrechnung an einem Auftrag, mit GiroCode." },
    grow: { quote: "Werkstatt, Montage, Angebote – und ich bin einer.", answer: "Stunden in der Werkstatt und bei der Montage am selben Auftrag." },
  },
  facade: {
    label: "Stuckateur",
    home: "survive",
    survive: { quote: "Unter Preis angenommen. Die Leasingrate wartet nicht.", answer: "Erst nachsehen, wer Ihnen noch Geld schuldet. Die offenen Posten stehen auf einen Blick da." },
    grow: { quote: "Das Gerüst steht schon wieder auf der falschen Baustelle.", answer: "Geräte mit eigenem Tagesplan. Sie sehen, wo was gerade steht." },
  },
  digger: {
    label: "Baggerfahrer",
    home: "grow",
    survive: { quote: "Der Bagger stand die ganze Woche.", answer: "Im Firmenplan sehen Sie früher, wann eine Woche leer bleibt." },
    grow: { quote: "Die Baggerstunden hat keiner aufgeschrieben.", answer: "Stunden per Handy am Auftrag erfassen. Sie landen im Büro als Grundlage für die Rechnung." },
  },
  developer: {
    label: "Bauträger",
    home: "grow",
    grow: { quote: "Drei Projekte, zehn Nachunternehmer. Das Budget läuft weg.", answer: "Soll-Ist-Vergleich, Nachträge und Kostenprognose pro Projekt. Die Abweichung sehen Sie laufend." },
  },
};

export const DE_CREW_ORDER = CREW_ORDER;

/** The two German world pages and how the home page names them. */
export const DE_WORLD_PAGES: Record<World, WorldPage> = {
  survive: {
    href: "/wenn-das-geld-knapp-ist/",
    door: "Das bin ich: Am Monatsende bleibt nichts übrig",
    title: "Wenn am Monatsende nichts übrig bleibt",
  },
  grow: {
    href: "/wenn-der-betrieb-waechst/",
    door: "Das bin ich: Der Betrieb wächst",
    title: "Wenn der Betrieb wächst – und der Papierkram mit",
  },
};
